/* Copyright (c) 2020-present CloudIO Inc */

import { Box } from '@mui/material';
import { MotionBox, MotionTypography } from 'custom/Motion';
import React, { useId } from 'react';
import { useTheme } from 'theme/Theme';

interface Props {
  disabled?: boolean;
  size?: number;
  switchWidth?: number;
  checkedLabel?: string;
  unCheckedLabel?: string;
  className?: string;
  checked: boolean;
  onChange: (e: React.MouseEvent<HTMLDivElement>) => void;
  'data-tip'?: string;
}

const spring = {
  type: 'spring',
  stiffness: 700,
  damping: 30,
};

function IOSSwitch({
  className,
  size = 24,
  switchWidth,
  checkedLabel,
  unCheckedLabel,
  disabled,
  checked,
  onChange,
  'data-tip': dataTip,
}: Props): React.ReactElement {
  const theme = useTheme();
  const id = useId();
  const gap = 4;
  const height = size + gap * 2;
  const handleSize = size;

  return (
    <Box
      className={className}
      data-tip={dataTip}
      onClick={disabled ? undefined : onChange}
      sx={{
        height: height,
        backgroundColor: checked
          ? theme.palette.primary.main
          : theme.palette.divider,
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: height / 2,
        padding: `${gap}px`,
        cursor: 'pointer',
        alignItems: 'center',
        gap: '8px',
        color: checked
          ? theme.palette.primary.contrastText
          : theme.palette.text.secondary,
        minWidth: switchWidth,
        userSelect: 'none',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {checked ? (
        <MotionTypography
          layout
          layoutId={id}
          transition={spring}
          sx={{ paddingLeft: '8px' }}
        >
          {checkedLabel}
        </MotionTypography>
      ) : null}
      <MotionBox
        sx={{
          width: handleSize,
          height: handleSize,
          backgroundColor: theme.palette.background.paper,
          borderRadius: handleSize / 2,
        }}
        layout
        transition={spring}
      />
      {checked ? null : (
        <MotionTypography
          layout
          layoutId={id}
          transition={spring}
          sx={{ paddingRight: '8px' }}
        >
          {unCheckedLabel}
        </MotionTypography>
      )}
    </Box>
  );
}

export default IOSSwitch;
